import {Component, Input, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';

import {ArrayOperationService} from '@services/administration/array-operation/array-operation.service';
import {SignalService} from '@services/administration/machine/signal.service';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {NotificationService} from '@services/notification/notification.service';

import {Signal} from '@models/dashboard/signal';
import {Machine} from '@models/machine/machine';

import {DATA_TYPE} from '@constants/data-type.constant';

import {
  CalculatedSignalEditModalComponent
} from '@components/administration/machine/machine-configuration/configuration/data-configuration/data-configuration-edit/calculated-signal-edit-modal/calculated-signal-edit-modal.component';
import {
  SignalEditModalComponent
} from '@components/administration/machine/machine-configuration/configuration/data-configuration/data-configuration-edit/signal-edit-modal/signal-edit-modal.component';
import {MachineTag} from '@models/adminstration/machine/tag/machine-tag';
import {Role} from '@models/user/role';
import {User} from '@models/user/user';
import {MachineTagService} from '@services/administration/machine/tag/machine-tag.service';
import {SettingsService} from '@services/administration/settings/settings.service';
import {
  InteractiveDataAnalysisService
} from '@services/left-menu/page/interactive-data-analysis/interactive-data-analysis.service';
import {ModalService} from '@services/modal/modal.service';
import {ConfirmationDialogComponent} from '@shared/confirmation-dialog/confirmation-dialog.component';
import {ModalDialogDismissOption} from '@shared/confirmation-dialog/modal-dialog-dismiss-option';

@Component({
  selector: 'scitis-admin-machine-data-configuration',
  templateUrl: './data-configuration.component.html',
  styleUrls: ['./data-configuration.component.scss'],
  standalone: false
})
export class DataConfigurationComponent implements OnInit {
  activeTab = 1;

  @Input()
  unCalculatedSignals: Signal[] = [];

  @Input()
  calculatedSignals: Signal[] = [];

  @Input()
  statusSignals: Signal[] = [];

  @Input()
  allSignals: Signal[] = [];

  @Input()
  userRole: Role = new Role(new User());

  @Input()
  machine: Machine = new Machine();
  @Input()
  machineTag: MachineTag | null = null;

  public isCommandModeDisabled = true;
  public deleteButtonText: string = '';
  private readonly ENABLE_COMMAND_WIDGET = 'ENABLE_COMMAND_WIDGET';
  private readonly COMPONENT_NAME: string = 'DataConfigurationComponent';

  constructor(
      private translate: TranslateService,
      private modalService: ModalService,
      private signalService: SignalService,
      private loadingIndicator: LoadingIndicatorService,
      private notification: NotificationService,
      private arrayOperationService: ArrayOperationService,
      private machineTagService: MachineTagService,
      private settingsService: SettingsService,
      private IDAService: InteractiveDataAnalysisService,
  ) {
  }

  public get isApplyToMachinesOfSameTagChecked() {
    if (this.machineTag === null) {
      return false;
    }
    return this.machineTag.applyToMachinesOfSameTag;
  }

  public get isApplyToMachinesOfSameTagVisible() {
    return !!this.machineTag;
  }

  public get isApplyToMachinesOfSameTagEnabled() {
    return (this.userRole.isTechAdminRole || this.userRole.isSuperAdminRole);
  }

  public get isLowerThanAdmin(): boolean {
    return !this.userRole.isAdminRole && !this.userRole.isSuperAdminRole && !this.userRole.isTechAdminRole;
  }

  public get isHigherThanAdmin(): boolean {
    return this.userRole.isSuperAdminRole || this.userRole.isTechAdminRole;
  }

  private static removeSignalFromList(signalsArray: Signal[], signal: Signal) {
    const index = signalsArray.indexOf(signal);
    if (index >= 0) {
      signalsArray.splice(index, 1);
    }
  }

  private static keepUsedInTriggersInformation(updatedSignal: Signal, savedSignal: Signal): void {
    savedSignal.usedInTriggers = updatedSignal.usedInTriggers;
  }

  ngOnInit() {
    this.setDeleteButtonTitleText();
    this.settingsService.findPortalSetting(this.ENABLE_COMMAND_WIDGET).subscribe({
      next: result => {
        if (result.value == 'TRUE') {
          this.isCommandModeDisabled = false;
        }
      }
    });
  }

  onAddSignal() {
    const modalRef = this.modalService.open(SignalEditModalComponent, {size: 'lg'});
    modalRef.componentInstance.signal = new Signal('', '', '', '');
    modalRef.componentInstance.machineId = this.machine.id;
    modalRef.componentInstance.allSignals = this.allSignals;
    modalRef.componentInstance.isHigherThanAdmin = this.isHigherThanAdmin;
    modalRef.componentInstance.isCommandModeDisabled = this.isCommandModeDisabled;
    modalRef.componentInstance.applyToMachinesOfSameTag = this.isApplyToMachinesOfSameTagChecked;
    modalRef.componentInstance.applyToMachinesOfSameTagMessage = this.getApplyToMachinesOfSameTagMessage();

    modalRef.result.then((result) => {
      this.loadingIndicator.show(this.translate.instant('Adding signal'));
      this.signalService.saveSignal(result).subscribe({
        next: (success) => {
          this.IDAService.dropCache();
          this.unCalculatedSignals.push(success);
          this.allSignals.push(success);
          let message = this.translate.instant('The signal: %signal was successfully saved');
          message = message.replaceAll('%signal', success.name.toBold());
          this.notification.success(message, this.COMPONENT_NAME, false);
          this.loadingIndicator.hide();
        }, error: (error) => {
          let errorMessage = this.translate.instant('There was an error saving signal %signal.');
          errorMessage = errorMessage.replaceAll('%signal', result.name.toBold());
          this.notification.error(errorMessage, error, this.COMPONENT_NAME, true);
          this.loadingIndicator.hide();
        }
      });
    });
  }

  onAddCalculatedSignal() {
    const modalRef = this.modalService.open(CalculatedSignalEditModalComponent, {size: 'lg'});
    modalRef.componentInstance.signal = new Signal('', '', '', '');
    modalRef.componentInstance.machineId = this.machine.id;
    modalRef.componentInstance.organizationId = this.machine.parentId;
    modalRef.componentInstance.allSignals = this.allSignals;
    modalRef.componentInstance.applyToMachinesOfSameTag = this.isApplyToMachinesOfSameTagChecked;
    modalRef.componentInstance.applyToMachinesOfSameTagMessage = this.getApplyToMachinesOfSameTagMessage();
    modalRef.componentInstance.isHigherThanAdmin = this.isHigherThanAdmin;
    modalRef.result.then((result) => {
      this.loadingIndicator.show(this.translate.instant('Adding signal'));
      this.signalService.saveSignal(result).subscribe({
        next: (success) => {
          this.calculatedSignals.push(success);
          this.allSignals.push(success);
          let message = this.translate.instant('The calculated signal: %signal was successfully saved');
          message = message.replaceAll('%signal', success.name.toBold());
          this.notification.success(message, this.COMPONENT_NAME, false);
          this.loadingIndicator.hide();
        }, error: (error) => {
          let errorMessage = this.translate.instant('There was an error saving calculated signal %signal.');
          errorMessage = errorMessage.replaceAll('%signal', result.name.toBold());
          this.notification.error(errorMessage, error, this.COMPONENT_NAME, true);
          this.loadingIndicator.hide();
        }
      });
    });
  }

  onRemoveSignal(signal: Signal) {
    if (this.machine.id) {
      this.loadingIndicator.show();
      this.signalService.getUsageInformationOfSignal(this.machine.id, signal.id).subscribe({
        next: result => {
          this.loadingIndicator.hide();
          let messageBody = this.translate.instant('Do you really want to permanently delete the signal %s ?').replace('%s', signal.name.toBold());
          let disableOkButton = false;
          if (result.length > 0) {
            messageBody = this.translate.instant('The signal cannot be deleted because it is used in the following areas: <br />') + result.join(', ').toBold();
            disableOkButton = true;
          }

          this.handleDeleteDialog(signal, messageBody, disableOkButton);
        }, error: () => {
          console.error('Error getting usage information of the signal');
          this.loadingIndicator.hide();
        }
      });
    }
  }

  onEdit(signal: Signal) {
    const modalRef = this.modalService.open(SignalEditModalComponent, {size: 'lg'});
    modalRef.componentInstance.signal = signal;
    modalRef.componentInstance.machineId = this.machine.id;
    modalRef.componentInstance.allSignals = this.allSignals;
    modalRef.componentInstance.isCommandModeDisabled = this.isCommandModeDisabled;
    modalRef.componentInstance.applyToMachinesOfSameTag = this.isApplyToMachinesOfSameTagChecked;
    modalRef.componentInstance.applyToMachinesOfSameTagMessage = this.getApplyToMachinesOfSameTagMessage();
    modalRef.componentInstance.isHigherThanAdmin = this.isHigherThanAdmin;
    modalRef.result.then((updatedSignal: Signal) => {
      this.loadingIndicator.show(this.translate.instant('Saving signal'));
      this.signalService.saveSignal(updatedSignal).subscribe({
        next: (savedSignal) => {
          this.IDAService.dropCache();
          DataConfigurationComponent.keepUsedInTriggersInformation(updatedSignal, savedSignal);
          this.updateSignal(savedSignal);
          let message = this.translate.instant('The signal: %signal was successfully updated');
          message = message.replaceAll('%signal', savedSignal.name.toBold());
          this.notification.success(message, this.COMPONENT_NAME, false);
          this.loadingIndicator.hide();
        }, error: (error) => {
          let errorMessage = this.translate.instant('There was an error saving signal %signal.');
          errorMessage = errorMessage.replaceAll('%signal', updatedSignal.name.toBold());
          this.notification.error(errorMessage, error, this.COMPONENT_NAME, true);
          this.loadingIndicator.hide();
        }
      });
    });
  }

  onEditCalculatedSignal(signal: Signal) {
    const modalRef = this.modalService.open(CalculatedSignalEditModalComponent, {size: 'lg'});
    modalRef.componentInstance.signal = signal;
    modalRef.componentInstance.machineId = this.machine.id;
    modalRef.componentInstance.organizationId = this.machine.parentId;
    modalRef.componentInstance.allSignals = this.allSignals;
    modalRef.componentInstance.applyToMachinesOfSameTag = this.isApplyToMachinesOfSameTagChecked;
    modalRef.componentInstance.applyToMachinesOfSameTagMessage = this.getApplyToMachinesOfSameTagMessage();
    modalRef.componentInstance.isHigherThanAdmin = this.isHigherThanAdmin;
    modalRef.result.then((result) => {
      this.loadingIndicator.show(this.translate.instant('Saving signal'));
      this.signalService.saveSignal(result).subscribe({
        next: (savedSignal: Signal) => {
          this.updateSignal(savedSignal);
          let message = this.translate.instant('The calculated signal %signal was successfully updated');
          message = message.replaceAll('%signal', savedSignal.name.toBold());
          this.notification.success(message, this.COMPONENT_NAME, false);
          this.loadingIndicator.hide();
        }, error: (error) => {
          let errorMessage = this.translate.instant('There was an error saving calculated signal %signal.');
          errorMessage = errorMessage.replaceAll('%signal', result.name.toBold());
          this.notification.error(errorMessage, error, this.COMPONENT_NAME, true);
          this.loadingIndicator.hide();
        }
      });
    });
  }

  getSignalScale(signal: Signal): number | string {
    if (signal.type === DATA_TYPE.STRING || signal.type === DATA_TYPE.BOOL) {
      return '-';
    } else {
      return signal.scale;
    }
  }

  getSignalDecimals(signal: Signal): number | string {
    if (signal.type !== DATA_TYPE.STRING && signal.type !== DATA_TYPE.BOOL) {
      return signal.decimals;
    } else {
      return '-';
    }
  }

  deleteButtonDisabled(signal: Signal): boolean {
    return (!this.userRole.isSuperAdminRole && !this.userRole.isTechAdminRole) || signal.protect || signal.signalUsed;
  }

  setDeleteButtonTitleText(): void {
    this.deleteButtonText = this.isLowerThanAdmin ? 'Only admins, tech admins and super admins can delete a signal' : 'Delete signal';
  }

  public onApplyToMachinesOfSameTagChange($event: any) {
    if (this.machineTag === null) {
      return;
    }
    this.loadingIndicator.show('Changing flag');
    this.machineTag.applyToMachinesOfSameTag = $event.target.checked;
    this.machineTagService.changeApplyToMachinesOfSameTag(this.machineTag.category, this.machineTag.tag, $event.target.checked).subscribe({
      next: (_result) => {
        let message: string = this.translate.instant('The ApplyToMachinesOfSameTag flag for tag %tag of category %category was updated successfully');
        message = message.replace('%category', this.machine.category.toBold());
        message = message.replace('%tag', this.machine.machineTag ? this.machine.machineTag.toBold() : '');
        this.notification.success(message, this.COMPONENT_NAME, true);
        this.loadingIndicator.hide();
      }, error: (error) => {
        let message: string = this.translate.instant('The ApplyToMachinesOfSameTag flag for tag %tag of category %category was not updated ');
        message = message.replace('%category', this.machine.category.toBold());
        message = message.replace('%tag', this.machine.machineTag ? this.machine.machineTag.toBold() : '');
        this.notification.error(message, error, this.COMPONENT_NAME, true);
        this.loadingIndicator.hide();
      }
    });
  }

  private updateSignal(updatedSignal: Signal): void {
    this.arrayOperationService.update(updatedSignal, this.allSignals, []);
    this.arrayOperationService.update(updatedSignal, this.unCalculatedSignals, []);
    this.arrayOperationService.update(updatedSignal, this.calculatedSignals, []);
    this.arrayOperationService.update(updatedSignal, this.statusSignals, []);
    for (const calcSignal of this.calculatedSignals) {
      if (calcSignal.signals) {
        this.arrayOperationService.update(updatedSignal, calcSignal.signals, []);
      }
    }
  }

  private deleteSignal(signal: Signal) {
    this.signalService.deleteSignal(this.machine.id, signal.id).subscribe({
      next: () => {
        this.notification.success(this.translate.instant('Signal') + ' ' + signal.name + ' ' +
            this.translate.instant('deleted') + '.', this.COMPONENT_NAME, false);
        console.log(signal);
        if (signal.calculated) {
          DataConfigurationComponent.removeSignalFromList(this.calculatedSignals, signal);
        } else {
          DataConfigurationComponent.removeSignalFromList(this.unCalculatedSignals, signal);
        }
        DataConfigurationComponent.removeSignalFromList(this.allSignals, signal);
        this.loadingIndicator.hide();
      }, error: (error) => {
        let message;
        if (error && error.status === 409) {
          message = this.translate.instant('Signal %signal cannot not be deleted. It is used in calculated signals.');
          message = message.replace('%signal', signal.name.toBold());
        } else {
          message = this.translate.instant('Signal could not be deleted') + ': ' + signal.name.toBold();
        }

        this.notification.error(message, error, this.COMPONENT_NAME, true);

        this.loadingIndicator.hide();
      }
    });
  }

  private deleteStatusSignal(signal: Signal) {
    this.signalService.deleteStatusSignal(this.machine.id, signal.id).subscribe({
      next: () => {
        this.notification.success(this.translate.instant('Signal') + ' ' + signal.name.toBold() + ' ' +
            this.translate.instant('deleted') + '.', this.COMPONENT_NAME, false);
        DataConfigurationComponent.removeSignalFromList(this.statusSignals, signal);
        this.loadingIndicator.hide();
      }, error: (error) => {
        this.notification.error(this.translate.instant('Status signal could not be deleted') + ': ' +
            signal.name.toBold(), error, this.COMPONENT_NAME, true);
        this.loadingIndicator.hide();
      }
    });
  }

  private getApplyToMachinesOfSameTagMessage(): string {
    let message = this.translate.instant('*Note. The signal will be added/updated on all machines with %tag tag of %category category! The Dashboard and User defined pages settings might need manual adjusting!');
    message = message.replace('%category', this.machine.category.toBold());
    message = message.replace('%tag', this.machine.machineTag ? this.machine.machineTag.toBold() : '');
    return message;
  }

  private handleDeleteDialog(signal: Signal, messageBody: string, disableOkButton: boolean) {
    const messageTitle = this.translate.instant('Delete signal');
    const messageOK = this.translate.instant('Yes');
    const messageCancel = this.translate.instant('Cancel');
    const modalRef = this.modalService.open(ConfirmationDialogComponent);
    modalRef.componentInstance.title = messageTitle;
    modalRef.componentInstance.body = messageBody;
    modalRef.componentInstance.okButtonText = messageOK;
    modalRef.componentInstance.cancelButtonText = messageCancel;
    modalRef.componentInstance.disableOkButton = disableOkButton;

    modalRef.result.then(() => {
    }, (reason) => {
      if (reason === ModalDialogDismissOption.OK) {
        this.loadingIndicator.show();
        if (signal.isStatusSignal) {
          this.deleteStatusSignal(signal);
        } else {
          this.deleteSignal(signal);
        }
      }
    });
  }
}
